import { useEffect, useMemo, useState, useRef, useCallback } from "react"
import { Mail, Phone, Upload, Lock, Trash2, ChevronRight, MapPin, CheckCircle2, X as XIcon, AlertTriangle, Pencil } from "lucide-react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { Button } from "@/clientWeb/components/ui/button"
import { Input } from "@/clientWeb/components/ui/input"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/clientWeb/components/ui/dialog"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { acceptAttributeFor, checkFileForUpload, getUploadPolicy } from "@/lib/upload-policy"
import { ButtonLoader } from "@/clientWeb/components/ui/page-loader"
import { OtpVerifyDialog } from "@/clientWeb/components/ui/otp-verify-dialog"
import { usePasswordRules } from "@/features/auth/hooks/usePasswordRules"
import PasswordStrengthMeter from "@/features/auth/components/PasswordStrengthMeter"
import { useAuthStore } from "@/features/auth/store/auth.store"
import { authService } from "@/features/auth/services/auth.service"
import { toAuthError } from "@/features/auth/services/auth-error"
import { AUTH_ERROR_MESSAGES } from "@/features/auth/constants/auth.constants"
import { profileService, profilePatch } from "./services/profile.service"
import { uploadAvatar, removeAvatar } from "./services/avatar.api"
import { PresenceStatusPicker } from "@/features/presence/components/PresenceStatusPicker"
import { PhoneCountrySelect } from "./components/PhoneCountrySelect"
import {
  DEFAULT_PHONE_COUNTRY,
  dialCodeOf,
  formatPhone,
  joinPhone,
  splitPhone,
  type CountryCode,
} from "./utils/phone"

const PASSWORD_MAX_LENGTH = 25

export default function MyProfile() {
  const t = useTranslation()
  const authUser = useAuthStore((s) => s.user)
  const setAuthAvatar = useAuthStore((s) => s.setAvatarUrl)
  const mergeAuthUser = useAuthStore((s) => s.mergeUser)
  const [isEditing, setIsEditing] = useState(false)
  const [showPasswordDialog, setShowPasswordDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  // Field errors hold translation keys, rendered with t() — a message already
  // on screen follows a language change instead of staying in the old one.
  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({})
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({})

  const newPwdRules = usePasswordRules(newPassword)

  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    location: '',
    avatarUrl: '',
  })

  useEffect(() => {
    let cancelled = false
    profileService.get().then((rec) => {
      if (cancelled) return
      setProfileData(rec)
      setTempProfileData(rec)
    })
    return () => { cancelled = true }
  }, [])

  const [tempProfileData, setTempProfileData] = useState(profileData)
  // The phone is edited as a picked country plus the number typed after it,
  // and saved as one international number (see utils/phone.ts).
  const [phoneCountry, setPhoneCountry] = useState<CountryCode>(DEFAULT_PHONE_COUNTRY)
  const [phoneNational, setPhoneNational] = useState('')
  const [imageError, setImageError] = useState(false)
  const [showAvatarDialog, setShowAvatarDialog] = useState(false)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  // The preview is a data URL for the confirm dialog only. The FILE is what we
  // upload — the old code kept just the data URL and stored that as the avatar,
  // which is why the picture never left this browser.
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [showRemoveAvatarDialog, setShowRemoveAvatarDialog] = useState(false)
  const [avatarRemoving, setAvatarRemoving] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [showOtpDialog, setShowOtpDialog] = useState(false)
  const [otpTarget, setOtpTarget] = useState({ value: "", type: "email" as "email" | "phone" })

  // Picker filter from the server's policy; empty (any image) until it loads.
  const [avatarAccept, setAvatarAccept] = useState("image/*")
  useEffect(() => {
    void getUploadPolicy().then(() => setAvatarAccept(acceptAttributeFor("avatar") || "image/*"))
  }, [])

  const handleAvatarFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error({ title: t("profile.invalidFileTitle"), description: t("profile.invalidFileDesc") })
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error({ title: t("profile.fileTooLargeTitle"), description: t("profile.fileTooLargeDesc") })
      return
    }
    const check = await checkFileForUpload(file, 'avatar')
    if (!check.ok) {
      toast.error({ title: t("profile.invalidFileTitle"), description: check.message })
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }
    const reader = new FileReader()
    reader.onload = (ev) => {
      setAvatarPreview(ev.target?.result as string)
      setAvatarFile(file)
      setShowAvatarDialog(true)
    }
    reader.readAsDataURL(file)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [t])

  const handleAvatarConfirm = async () => {
    if (!avatarFile) {
      setShowAvatarDialog(false)
      return
    }
    setAvatarUploading(true)
    try {
      const avatarUrl = await uploadAvatar(avatarFile)
      setProfileData(prev => ({ ...prev, avatarUrl }))
      setTempProfileData(prev => ({ ...prev, avatarUrl }))
      setImageError(false)
      // Keep the header and every other surface in step without a reload —
      // they all read the auth store, not this component's state.
      setAuthAvatar(avatarUrl)
      setShowAvatarDialog(false)
      setAvatarPreview(null)
      setAvatarFile(null)
      toast.success({ title: t("profile.photoUpdatedTitle"), description: t("profile.photoUpdatedDesc") })
    } catch (err) {
      // Stay on the dialog so the picked image isn't lost to a transient failure.
      toast.error({
        title: t("profile.photoUploadFailedTitle"),
        description: err instanceof Error && err.message ? err.message : t("profile.photoUploadFailedDesc"),
      })
    } finally {
      setAvatarUploading(false)
    }
  }

  const handleAvatarRemove = async () => {
    setAvatarRemoving(true)
    try {
      await removeAvatar()
      setProfileData(prev => ({ ...prev, avatarUrl: '' }))
      setTempProfileData(prev => ({ ...prev, avatarUrl: '' }))
      setImageError(false)
      setAuthAvatar(null)
      setShowRemoveAvatarDialog(false)
      toast.success({ title: t("profile.photoRemovedTitle"), description: t("profile.photoRemovedDesc") })
    } catch (err) {
      toast.error({
        title: t("profile.photoRemoveFailedTitle"),
        description: err instanceof Error && err.message ? err.message : t("profile.photoRemoveFailedDesc"),
      })
    } finally {
      setAvatarRemoving(false)
    }
  }

  const validateProfile = (): boolean => {
    const errs: Record<string, string> = {}
    const name = tempProfileData.name.trim()
    if (!name) {
      errs.name = 'profile.validation.nameRequired'
    } else if (name.length < 2) {
      errs.name = 'profile.validation.nameMin2'
    } else if (name.length > 100) {
      errs.name = 'profile.validation.nameMax100'
    }
    if (tempProfileData.role.trim().length > 100) {
      errs.role = 'profile.validation.roleMax100'
    }
    // Phone and location are optional. They used to be required, which only
    // ever passed because both were pre-filled with sample values. A typed
    // phone must be a valid number for the picked country.
    if (joinPhone(phoneCountry, phoneNational) === null) {
      errs.phone = 'profile.validation.phoneInvalid'
    }
    if (tempProfileData.location.trim().length > 100) {
      errs.location = 'profile.validation.locationMax100'
    }
    setProfileErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleEdit = async () => {
    if (isEditing) {
      if (!validateProfile()) return
      setIsSaving(true)
      try {
        const phone = joinPhone(phoneCountry, phoneNational) ?? ''
        const saved = await profileService.update(profilePatch(profileData, { ...tempProfileData, phone }))
        setProfileData(saved)
        // Same as the avatar: the header and Chat Settings read the auth store,
        // not this component, so without this they kept the old name until a reload.
        if (saved.name) mergeAuthUser({ name: saved.name })
        setIsEditing(false)
        setProfileErrors({})
        toast.success({ title: t("profile.profileUpdatedTitle"), description: t("profile.profileUpdatedDesc") })
      } catch (err) {
        toast.error({ title: t("common.error"), description: err instanceof Error && err.message ? err.message : t("profile.updateFailedDesc") })
      } finally {
        setIsSaving(false)
      }
    } else {
      setTempProfileData(profileData)
      const { country, national } = splitPhone(profileData.phone)
      setPhoneCountry(country)
      setPhoneNational(national)
      setProfileErrors({})
      setIsEditing(true)
    }
  }

  const handleCancel = () => {
    setTempProfileData(profileData)
    setIsEditing(false)
    setProfileErrors({})
    toast({ title: t("profile.changesDiscarded"), variant: 'warning' })
  }

  const validatePassword = (): boolean => {
    const errs: Record<string, string> = {}
    if (!currentPassword) {
      errs.currentPassword = 'profile.validation.currentPasswordRequired'
    }
    if (!newPassword) {
      errs.newPassword = 'profile.validation.newPasswordRequired'
    } else if (!newPwdRules.isSecure) {
      errs.newPassword = 'profile.validation.newPasswordWeak'
    } else if (newPassword === currentPassword) {
      errs.newPassword = 'profile.validation.newPasswordSameAsCurrent'
    }
    if (!confirmPassword) {
      errs.confirmPassword = 'profile.validation.confirmPasswordRequired'
    } else if (confirmPassword !== newPassword) {
      errs.confirmPassword = 'profile.validation.passwordsDoNotMatch'
    }
    setPasswordErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleChangePassword = async () => {
    if (!validatePassword()) return
    if (!authUser?.email) {
      toast.error({ title: t('common.error'), description: t('auth.loginRequired') })
      return
    }
    setIsChangingPassword(true)
    try {
      const email = authUser?.email ?? profileData.email
      await authService.changePassword(email, currentPassword, newPassword)
      setIsChangingPassword(false)
      setShowPasswordDialog(false)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setPasswordErrors({})
      toast.success({ title: t("profile.passwordChangedTitle"), description: t("profile.passwordChangedDesc") })
    } catch (err) {
      setIsChangingPassword(false)
      const authErr = toAuthError(err)
      if (authErr.code === 'INVALID_PASSWORD') {
        setPasswordErrors((p) => ({ ...p, currentPassword: 'profile.validation.currentPasswordIncorrect' }))
        return
      } else if (authErr.code === 'WEAK_PASSWORD') {
        setPasswordErrors((p) => ({ ...p, newPassword: 'profile.validation.newPasswordWeak' }))
        return
      }
      // The sentence follows the language; a message the server wrote itself
      // (not the code's stock English) is kept beside it as the detail.
      const stock = authErr.message === AUTH_ERROR_MESSAGES[authErr.code]
      const reason = t(`auth.errors.${authErr.code}`)
      toast.error({
        title: t('common.error'),
        description: stock ? reason : `${reason} ${t('auth.errorDetail', { detail: authErr.message })}`,
      })
    }
  }

  const initials = useMemo(() => {
    if (!profileData.name) return ""
    return profileData.name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0]?.toUpperCase())
      .join("")
  }, [profileData.name])

  const infoChips = [
    {
      label: t("profile.email"),
      value: profileData.email,
      icon: <Mail className="w-4 h-4" />,
      gradient: "from-blue-400 via-blue-500 to-blue-600",
      iconBg: "bg-blue-200 dark:bg-blue-500/25",
      cardBg: "bg-gradient-to-br from-blue-50/10 to-blue-100/10 dark:from-blue-900/10 dark:to-blue-800/10",
      borderColor: "border-blue-200/30 dark:border-blue-700/20",
      shadowColor: "shadow-blue-400/5",
    },
    {
      label: t("profile.phone"),
      value: formatPhone(profileData.phone),
      icon: <Phone className="w-4 h-4" />,
      gradient: "from-emerald-400 via-emerald-500 to-emerald-600",
      iconBg: "bg-emerald-200 dark:bg-emerald-500/25",
      cardBg: "bg-gradient-to-br from-emerald-50/10 to-emerald-100/10 dark:from-emerald-900/10 dark:to-emerald-800/10",
      borderColor: "border-emerald-200/30 dark:border-emerald-700/20",
      shadowColor: "shadow-emerald-400/5",
    },
    {
      label: t("profile.location"),
      value: profileData.location,
      icon: <MapPin className="w-4 h-4" />,
      gradient: "from-purple-400 via-purple-500 to-purple-600",
      iconBg: "bg-purple-200 dark:bg-purple-500/25",
      cardBg: "bg-gradient-to-br from-purple-50/10 to-purple-100/10 dark:from-purple-900/10 dark:to-purple-800/10",
      borderColor: "border-purple-200/10 dark:border-purple-700/10",
      shadowColor: "shadow-purple-400/5",
    },
  ]

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Page Header */}
      <div className="space-y-1">

        <h2 className="text-xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white">
          {t("profile.title")}
        </h2>
        <p className="text-muted-foreground text-sm mt-1">
          {t("profile.subtitle")}
        </p>
      </div>

      {/* Hero profile card */}
      {/* <RibbonBackground className="shadow-md border border-slate-200 dark:border-border dark:border-slate-800 rounded-3xl "> */}
      <div className="relative border shadow-md border-stroke-100 dark:border-border shadow-sm rounded-xl bg-gradient-to-br from-slate-50 to-white-100 dark:from-[#2E2E2E] dark:to-[#3A3A3A] overflow-hidden text-slate-900 dark:text-foreground/80 dark:text-white animate-in fade-in slide-in-from-top-2">
        {/* <div className="absolute inset-0" /> */}
        <div className="absolute inset-0 opacity-50 pointer-events-none">
          <div className="w-72 h-72 bg-primary/5 rounded-full blur-3xl absolute -top-16 -right-10 animate-pulse" />
          <div className="w-64 h-64 bg-primary/10 rounded-full blur-[80px] absolute bottom-0 -left-16 animate-[pulse_8s_ease-in-out_infinite]" />
        </div>

        <div className="relative p-4 flex flex-col gap-6">
          <div className="flex flex-row items-center gap-4 lg:flex-row lg:items-center lg:gap-6">
            <div className="relative group w-fit shrink-0">
              <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-3xl bg-gradient-to-br from-primary/5 to-primary/20 backdrop-blur-sm shadow-2xl flex items-center justify-center overflow-hidden border border-white/50 dark:border-white/10 transition-all duration-500 group-hover:rotate-2">
                {profileData.avatarUrl && !imageError ? (
                  <img
                    src={profileData.avatarUrl}
                    alt={profileData.name}
                    className="w-full h-full object-cover"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <span className="text-3xl font-semibold tracking-wide">{initials}</span>
                )}
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute -bottom-1 -right-1 w-10 h-10 rounded-full bg-primary text-white shadow-lg flex items-center justify-center hover:scale-105 transition-transform z-10"
                title={t("profile.changeProfilePicture")}
              >
                <Upload className="w-5 h-5" />
              </button>
              {profileData.avatarUrl && (
                <button
                  onClick={() => setShowRemoveAvatarDialog(true)}
                  className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-destructive text-white shadow-lg flex items-center justify-center hover:scale-105 transition-transform z-10"
                  title={t("profile.removeProfilePicture")}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={avatarAccept}
                className="hidden"
                onChange={handleAvatarFileChange}
              />
            </div>

            <div className="flex-1 min-w-0 space-y-1">
              {/* <p className="text-xs text-slate-400 dark:text-white/70">
                {t("profile.profileLabel")}
              </p> */}
              <h3 className="text-xl sm:text-2xl font-semibold leading-tight truncate">
                {profileData.name}
              </h3>
              <p className="text-sm text-slate-600 dark:text-white/80 truncate">
                {profileData.role}
              </p>
              <div className="pt-1">
                <PresenceStatusPicker />
              </div>
            </div>

            {!isEditing && (
              <div className="shrink-0 flex items-center gap-2">
                <Button
                  onClick={handleEdit}
                  size="icon"
                  className="bg-primary text-white hover:bg-primary/90 sm:hidden"
                  title={t("profile.editProfile")}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  onClick={handleEdit}
                  className="hidden sm:inline-flex bg-primary text-white hover:bg-primary/90"
                >
                  {t("profile.editProfile")}
                </Button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {infoChips.map(({ label, value, icon, iconBg, cardBg, borderColor, shadowColor }) => (
              <div
                key={label}
                className={`flex items-center gap-3 rounded-2xl ${cardBg} backdrop-blur-sm p-2 ${borderColor} border text-left transition-all duration-300 hover:scale-105 hover:${shadowColor} hover:shadow-lg`}
              >
                <div className={`w-8 h-8 rounded-xl ${iconBg} text-black dark:text-foreground/60 flex items-center justify-center shadow-lg`}>
                  {icon}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs tracking-wide text-slate-600 dark:text-slate-400 font-semibold">
                    {label}
                  </p>
                  <p className="text-xs pt-1 font-semibold truncate text-slate-900 dark:text-foreground/80 dark:text-white">
                    {value || t("profile.notAdded")}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* </RibbonBackground> */}

      {/* Editing form */}
      {isEditing && (
        <div className="bg-card dark:bg-card/70 rounded-2xl border border-slate-200 dark:border-border dark:border-slate-800 shadow-lg p-6 space-y-6 animate-in fade-in slide-in-from-bottom-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-sm font-semibold text-slate-600 dark:text-slate-300">{t("profile.fullName")}</label>
              <Input
                value={tempProfileData.name}
                onChange={e => { setTempProfileData({ ...tempProfileData, name: e.target.value }); if (profileErrors.name) setProfileErrors(p => ({ ...p, name: '' })) }}
                placeholder={t("auth.namePlaceholder")}
                className={profileErrors.name ? 'border-red-500 focus-visible:ring-red-500' : ''}
              />
              {profileErrors.name && <p className="text-xs text-red-500 mt-1">{t(profileErrors.name)}</p>}
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600 dark:text-slate-300">{t("profile.role")}</label>
              <Input
                value={tempProfileData.role}
                onChange={e => { setTempProfileData({ ...tempProfileData, role: e.target.value }); if (profileErrors.role) setProfileErrors(p => ({ ...p, role: '' })) }}
                placeholder={t("profile.rolePlaceholder")}
                className={profileErrors.role ? 'border-red-500 focus-visible:ring-red-500' : ''}
              />
              {profileErrors.role && <p className="text-xs text-red-500 mt-1">{t(profileErrors.role)}</p>}
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600 dark:text-slate-300">{t("profile.phone")}</label>
              <div className="flex gap-2">
                <PhoneCountrySelect
                  value={phoneCountry}
                  onChange={c => { setPhoneCountry(c); if (profileErrors.phone) setProfileErrors(p => ({ ...p, phone: '' })) }}
                />
                <Input
                  type="tel"
                  inputMode="tel"
                  value={phoneNational}
                  onChange={e => { setPhoneNational(e.target.value); if (profileErrors.phone) setProfileErrors(p => ({ ...p, phone: '' })) }}
                  placeholder={t("profile.phonePlaceholder")}
                  className={`flex-1 min-w-0${profileErrors.phone ? ' border-red-500 focus-visible:ring-red-500' : ''}`}
                />
                <Button
                  type="button" variant="outline" size="sm" className="shrink-0"
                  onClick={() => {
                    const typed = joinPhone(phoneCountry, phoneNational)
                    setOtpTarget({ value: typed ? formatPhone(typed) : `${dialCodeOf(phoneCountry)} ${phoneNational}`.trim(), type: 'phone' })
                    setShowOtpDialog(true)
                  }}
                >
                  {t("common.verify")}
                </Button>
              </div>
              {profileErrors.phone && <p className="text-xs text-red-500 mt-1">{t(profileErrors.phone)}</p>}
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600 dark:text-slate-300">{t("profile.location")}</label>
              <Input
                value={tempProfileData.location}
                onChange={e => { setTempProfileData({ ...tempProfileData, location: e.target.value }); if (profileErrors.location) setProfileErrors(p => ({ ...p, location: '' })) }}
                placeholder={t("profile.locationPlaceholder")}
                className={profileErrors.location ? 'border-red-500 focus-visible:ring-red-500' : ''}
              />
              {profileErrors.location && <p className="text-xs text-red-500 mt-1">{t(profileErrors.location)}</p>}
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-semibold text-slate-600 dark:text-slate-300">{t("profile.email")}</label>
              <div className="flex gap-2">
                <Input value={tempProfileData.email} disabled className="opacity-70 flex-1" />
                <Button
                  type="button" variant="outline" size="sm" className="shrink-0"
                  onClick={() => { setOtpTarget({ value: tempProfileData.email, type: 'email' }); setShowOtpDialog(true) }}
                >
                  {t("common.verify")}
                </Button>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="border-slate-200 dark:border-border dark:border-border"
            >
              {t("common.discard")}
            </Button>
            <Button onClick={handleEdit} disabled={isSaving} className="bg-primary hover:bg-primary/90 min-w-[120px]">
              {isSaving ? <ButtonLoader /> : t("common.saveChanges")}
            </Button>
          </div>
        </div>
      )}

      {/* Account Actions */}
      <div className="space-y-4">
        <h3 className="text-md font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">
          {t("settings.accountActions")}
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Change Password */}
          <button
            onClick={() => setShowPasswordDialog(true)}
            className="flex items-center justify-between p-4 rounded-xl border border-stroke-100 dark:border-border dark:border-border hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-all duration-200 group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Lock className="w-5 h-5 text-primary" />
              </div>
              <div className="text-left">
                <p className="font-medium text-md text-slate-900 dark:text-foreground/80 dark:text-white">
                  {t("settings.changePassword")}
                </p>
                <p className="text-xs pt-1 text-slate-500 dark:text-slate-400">
                  {t("settings.changePasswordDesc")}
                </p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300" />
          </button>

          {/* Delete Account */}
          <button
            onClick={() => setShowDeleteDialog(true)}
            className="flex items-center justify-between p-4 rounded-xl border border-red-100 dark:border-red-900/30 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200 group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="text-left">
                <p className="font-medium text-md text-red-600 dark:text-red-400">
                  {t("settings.deleteAccount")}
                </p>
                <p className="text-xs pt-1 text-slate-500 dark:text-slate-400">
                  {t("settings.deleteAccountDesc")}
                </p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-red-400 group-hover:text-red-600" />
          </button>
        </div>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={(open) => { setShowPasswordDialog(open); if (!open) { setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); setPasswordErrors({}) } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("settings.changePassword")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                {t("profile.currentPassword")}
              </label>
              <Input
                type="password"
                placeholder={t("auth.passwordPlaceholder")}
                value={currentPassword}
                onChange={e => { setCurrentPassword(e.target.value.slice(0, PASSWORD_MAX_LENGTH)); if (passwordErrors.currentPassword) setPasswordErrors(p => ({ ...p, currentPassword: '' })) }}
                className={passwordErrors.currentPassword ? 'border-red-500 focus-visible:ring-red-500' : ''}
                maxLength={PASSWORD_MAX_LENGTH}
              />
              {currentPassword.length >= PASSWORD_MAX_LENGTH && (
                <p className="text-xs text-muted-foreground mt-1">{t("profile.maxCharsReached", { max: PASSWORD_MAX_LENGTH })}</p>
              )}
              {passwordErrors.currentPassword && <p className="text-xs text-red-500 mt-1">{t(passwordErrors.currentPassword)}</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                {t("profile.newPassword")}
              </label>
              <Input
                type="password"
                placeholder={t("auth.passwordPlaceholder")}
                value={newPassword}
                onChange={e => { setNewPassword(e.target.value.slice(0, PASSWORD_MAX_LENGTH)); if (passwordErrors.newPassword) setPasswordErrors(p => ({ ...p, newPassword: '' })) }}
                className={passwordErrors.newPassword ? 'border-red-500 focus-visible:ring-red-500' : ''}
                maxLength={PASSWORD_MAX_LENGTH}
              />
              {newPassword.length >= PASSWORD_MAX_LENGTH && (
                <p className="text-xs text-muted-foreground mt-1">{t("profile.maxCharsReached", { max: PASSWORD_MAX_LENGTH })}</p>
              )}
              {newPassword && (
                <PasswordStrengthMeter
                  score={newPwdRules.score}
                  checks={newPwdRules.checks}
                  isSecure={newPwdRules.isSecure}
                  variant="full"
                />
              )}
              {passwordErrors.newPassword && <p className="text-xs text-red-500 mt-1">{t(passwordErrors.newPassword)}</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                {t("profile.confirmNewPassword")}
              </label>
              <Input
                type="password"
                placeholder={t("auth.confirmPassword")}
                value={confirmPassword}
                onChange={e => { setConfirmPassword(e.target.value.slice(0, PASSWORD_MAX_LENGTH)); if (passwordErrors.confirmPassword) setPasswordErrors(p => ({ ...p, confirmPassword: '' })) }}
                className={passwordErrors.confirmPassword ? 'border-red-500 focus-visible:ring-red-500' : ''}
                maxLength={PASSWORD_MAX_LENGTH}
              />
              {confirmPassword.length >= PASSWORD_MAX_LENGTH && (
                <p className="text-xs text-muted-foreground mt-1">{t("profile.maxCharsReached", { max: PASSWORD_MAX_LENGTH })}</p>
              )}
              {passwordErrors.confirmPassword && <p className="text-xs text-red-500 mt-1">{t(passwordErrors.confirmPassword)}</p>}
            </div>
            <Button onClick={handleChangePassword} disabled={isChangingPassword} className="w-full bg-primary hover:bg-primary/90">
              {isChangingPassword ? <ButtonLoader /> : t("profile.updatePassword")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Avatar Preview / Confirm Dialog */}
      <Dialog open={showAvatarDialog} onOpenChange={setShowAvatarDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("profile.updateProfilePhotoTitle")}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-5 py-2">
            {avatarPreview && (
              <div className="w-36 h-36 rounded-3xl overflow-hidden border-4 border-primary/20 shadow-xl">
                <img src={avatarPreview} alt={t("profile.photoPreviewAlt")} className="w-full h-full object-cover" />
              </div>
            )}
            <p className="text-sm text-muted-foreground text-center">
              {t("profile.useThisPhotoQuestion")}
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" disabled={avatarUploading} onClick={() => { setShowAvatarDialog(false); setAvatarPreview(null); setAvatarFile(null) }}>
              <XIcon className="w-4 h-4 mr-1.5" />{t("common.cancel")}
            </Button>
            {/* Disabled while in flight: the upload is a real round trip now,
                and a double-click would push the same picture to S3 twice. */}
            <Button className="flex-1" onClick={handleAvatarConfirm} disabled={avatarUploading}>
              {avatarUploading
                ? <ButtonLoader />
                : <><CheckCircle2 className="w-4 h-4 mr-1.5" />{t("profile.usePhoto")}</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove Avatar Confirm Dialog */}
      <Dialog open={showRemoveAvatarDialog} onOpenChange={(open) => { if (!avatarRemoving) setShowRemoveAvatarDialog(open) }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("profile.removeProfilePicture")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground text-center py-2">
            {t("profile.removePhotoQuestion")}
          </p>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" disabled={avatarRemoving} onClick={() => setShowRemoveAvatarDialog(false)}>
              <XIcon className="w-4 h-4 mr-1.5" />{t("common.cancel")}
            </Button>
            <Button variant="destructive" className="flex-1" onClick={handleAvatarRemove} disabled={avatarRemoving}>
              {avatarRemoving
                ? <ButtonLoader />
                : <><Trash2 className="w-4 h-4 mr-1.5" />{t("profile.removePhoto")}</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 dark:text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              {t("settings.deleteAccount")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30 rounded-xl p-4 text-sm text-red-800 dark:text-red-300">
              <p className="font-semibold mb-1">{t("settings.deleteAccountWarningTitle")}</p>
              <p>{t("settings.deleteAccountWarningDesc")}</p>
            </div>

            <div className="space-y-2 text-sm text-slate-600 dark:text-slate-400 px-1">
              <p>{t("settings.deleteAccountListTitle")}</p>
              <ul className="list-disc pl-5 space-y-1 marker:text-slate-400">
                <li>{t("settings.deleteAccountListItem1")}</li>
                <li>{t("settings.deleteAccountListItem2")}</li>
                <li>{t("settings.deleteAccountListItem3")}</li>
                <li>{t("settings.deleteAccountListItem4")}</li>
              </ul>
            </div>

            <div className="flex gap-3 pt-4 border-t border-stroke-100 dark:border-border dark:border-border mt-2">
              <Button
                onClick={() => setShowDeleteDialog(false)}
                variant="outline"
                className="flex-1"
              >
                {t("common.cancel")}
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => {
                  setShowDeleteDialog(false)
                  toast.error({ title: t("settings.accountDeletionRequestedTitle"), description: t("settings.accountDeletionRequestedDesc") })
                }}
              >
                {t("profile.deleteAccountBtn")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* OTP Verification Dialog */}
      <OtpVerifyDialog
        open={showOtpDialog}
        onOpenChange={setShowOtpDialog}
        target={otpTarget.value}
        type={otpTarget.type}
        onVerified={() =>
          toast.success({
            title: t("common.verified"),
            description: otpTarget.type === "email" ? t("profile.emailVerifiedDesc") : t("profile.phoneVerifiedDesc"),
          })
        }
      />
    </div>
  )
}
